import axios, { CancelTokenSource } from 'axios'
import moment from 'moment'
import { useRef, useState } from 'react'
import { LoaderFunctionArgs, useLoaderData, useParams } from 'react-router'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { BaseRecord } from '../../models/BaseRecord'
import { Message } from '../../models/Message'
import { RecordFactory } from '../../models/RecordFactory'
import { Team } from '../../models/Team'
import { User } from '../../models/User'
import { generateCancelToken } from '../../modules/apiclient'
import { PSApplication } from '../app/PSApplication'
import { ConversationView } from './ConversationView'
import { MessageInputBar } from './MessageInputBar'
import { getUpdatedInbox } from './messagesDataTransforms'
import { SQLiteFactory } from "../../models/SQLiteFactory";

type SearchState<T> = {
    searchText: string
    isSearching: boolean
    didSearch: boolean
    searchResults: T[]
}

type ConversationState = {
    isLoading: boolean
    currentThread: BaseRecord | null
    messages: Message[]
    inputBarHeight: number
    showNewMessage: boolean
    showCreateGroup: boolean
    newConversation: SearchState<User> & {
        selectedUsers: User[]
    }
    pagination: {
        justScrolled: boolean
        hasMore: boolean
        timestamp?: moment.Moment
    }
    error?: string
}

type MessagesViewData = {
    inbox: Message[]
    messages: Message[]
    thread: BaseRecord | null
    unreadCount: number
    inboxTimestamp?: moment.Moment
    conversationTimestamp?: moment.Moment
    error?: string
}

export const loader = async ({ params }: LoaderFunctionArgs): Promise<MessagesViewData> => {
    const data: MessagesViewData = {
        inbox: [],
        messages: [],
        thread: null,
        unreadCount: 0,
    }

    const { threadID } = params

    const result = await RecordFactory.getInstance().fetchInbox(undefined, undefined)
    if (result.ok) {
        data.inbox = result.value
        data.inboxTimestamp = result.value.length
            ? result.value[result.value.length - 1].threadLastModifiedDate
            : undefined
    }
    else {
        throw result.error
    }

    data.unreadCount = (await SQLiteFactory.getInstance().badgeCount()) || 0

    if (threadID) {
        const thread =
            (await RecordFactory.getInstance().loadUser(threadID)) ||
            (await RecordFactory.getInstance().loadTeam(threadID))

        if (thread) {
            const result = await RecordFactory.getInstance().fetchThread(thread)

            if (!result.ok) {
                data.error = (result.error as any).toString()
                throw result.error
            }

            data.messages = result.value
            data.thread = thread
            data.conversationTimestamp = result.value.length
                ? result.value[result.value.length - 1].createdDate
                : undefined
        }
    }

    // If we are coming from another page and opening messages,
    // we need to mark the current selected thread as _read_,
    // but only if the last message in the thread has not been viewed.
    if (data.messages.length) {
        const lastMessage = data.messages[0]
        const isInbound = PSApplication.me().id !== lastMessage.sender?.id
        const isUnread = !lastMessage.viewedDate

        if (isInbound && isUnread) {
            const result = await RecordFactory.getInstance().markRead(lastMessage)
            if (result.ok) {
                data.unreadCount = result.value.badge || 0
                data.inbox = result.value.messages?.length
                    ? getUpdatedInbox(
                          data.inbox,
                          result.value.messages[0],
                          data.thread instanceof Team
                      )
                    : []
            }
            else {
                throw result.error
            }
        }
    }

    return data
}

const ThreadView = () => {
    const data = useLoaderData() as Awaited<ReturnType<typeof loader>>

    const { logAndDisplayError } = useErrorHandler()

    const [conversationState, setConversationState] = useState<ConversationState>({
        isLoading: false,
        currentThread: data.thread,
        messages: data.messages,
        error: data.error,
        inputBarHeight: 20,
        showNewMessage: false,
        showCreateGroup: false,
        newConversation: {
            searchText: '',
            isSearching: false,
            didSearch: false,
            searchResults: [],
            selectedUsers: [],
        },
        pagination: {
            justScrolled: false,
            hasMore: true,
            timestamp: data.conversationTimestamp,
        },
    })

    const { currentThread, inputBarHeight } = conversationState

    const inboxState = {
        isSending: false,
        inbox: data.inbox,
        unreadCount: data.unreadCount,
        searchText: '',
        isSearching: false,
        didSearch: false,
        searchResults: [],
        pagination: {
            isScrolling: false,
            hasMore: true,
            timestamp: data.inboxTimestamp,
        },
    }

    const isFetchingThreadRef = useRef(false)
    const cancelThreadFetchTokenRef = useRef<CancelTokenSource | null>(null)

    const cancelPreviousThreadFetchRequest = () => {
        if (cancelThreadFetchTokenRef.current) {
            cancelThreadFetchTokenRef.current.cancel(
                'Previous thread fetch request cancelled due to new request.'
            )
        }
        cancelThreadFetchTokenRef.current = generateCancelToken()
    }

    const fetchThread = async (thread: BaseRecord, createdBeforeDate?: moment.Moment) => {
        isFetchingThreadRef.current = true

        cancelPreviousThreadFetchRequest()
        setConversationState((prev) => ({ ...prev, isLoading: true }))

        const result = await RecordFactory.getInstance().fetchThread(
            thread,
            undefined,
            createdBeforeDate,
            cancelThreadFetchTokenRef.current?.token
        )

        isFetchingThreadRef.current = false

        if (!result.ok) {
            if (axios.isCancel(result.error)) {
                console.log('Cancelled thread fetch:', (result.error as any).message)
            }
            else {
                logAndDisplayError(result.error)
                setConversationState((prev) => ({
                    ...prev,
                    error: (result.error as any).toString(),
                    isLoading: false,
                }))
            }
            return
        }

        const hasMore = result.value.length > 0
        const lastMessageTimestamp = hasMore
            ? result.value[result.value.length - 1].createdDate
            : undefined

        setConversationState((prev) => ({
            ...prev,
            isLoading: false,
            messages: [...prev.messages, ...result.value],
            pagination: {
                ...prev.pagination,
                justScrolled: prev.messages.length > 0,
                hasMore,
                timestamp: lastMessageTimestamp,
            },
        }))
    }

    const loadMoreConversation = () => {
        const { hasMore, timestamp } = conversationState.pagination

        if (!isFetchingThreadRef.current && hasMore && currentThread) {
            fetchThread(currentThread, timestamp)
        }
    }

    return (
        <>
            <ConversationView
                conversationState={conversationState}
                inboxState={inboxState}
                onLoadMore={loadMoreConversation}
            />
            {currentThread && (
                <MessageInputBar
                    currentThread={currentThread}
                    searchText={inboxState.searchText}
                    inputBarHeight={inputBarHeight}
                    onUpdateInputBarHeight={(height) =>
                        setConversationState((prev) => ({
                            ...prev,
                            inputBarHeight: height,
                        }))
                    }
                />
            )}
        </>
    )
}

// Unfortunately, the ThreadView component isn't properly implemented and doesn't
// respond to changes in inputs. In the interests of time, I'm using this hack to
// just completely remount the component when anything about the thread changes.
export const MessageThreadWrapper = () => {
    const data = useLoaderData() as Awaited<ReturnType<typeof loader>>

    return <ThreadView key={JSON.stringify(data)} />
}
